import { CancelRunDialog } from './CancelRunDialog'
import { WorkflowRunFilters } from './WorkflowRunFilters'
import { WorkflowRunRow } from './WorkflowRunRow'
import { WorkflowRunsErrorState } from './WorkflowRunsErrorState'
import { Pagination } from '@/components/ui/pagination'
import { SortableTableHead } from '@/components/projects/SortableTableHead'
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useApprovePhase, useAuth, useCancelWorkflowRun, useDebounce, useNowTick, usePagination, useRejectPhase, useRetryWorkflowRun, useWorkflowDefinitions, useWorkflowRuns } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import { extractValidationMessage } from '@/lib/validationErrors'
import { getErrorMessage } from '@/api/errors'
import type { WorkflowRunFilters as RunFiltersType, WorkflowRun, WorkflowRunStatus } from '@/types'
import { isActiveRunStatus, WORKFLOW_RUN_DEFAULT_SORT_DIRECTIONS, type WorkflowRunSortColumn } from '@/types'
import { workflowRunStatusFilterOptions } from '../shared'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

const SORT_KEYS = ['sort', 'sortBy'] as const

const STATUS_KEY = 'status'

const STATUS_ORDER: string[] = workflowRunStatusFilterOptions.map(o => o.value).filter(Boolean)

function inOptionOrder(statuses: WorkflowRunStatus[]): WorkflowRunStatus[] {
  return [...statuses].sort((a, b) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b))
}

function readStoredStatuses(storageKey: string): WorkflowRunStatus[] {
  const saved = new URLSearchParams(sessionStorage.getItem(storageKey) ?? '')
  return inOptionOrder(saved.getAll(STATUS_KEY).filter((s): s is WorkflowRunStatus => STATUS_ORDER.includes(s)))
}

function storeStatuses(storageKey: string, statuses: WorkflowRunStatus[]) {
  const params = new URLSearchParams()
  statuses.forEach(s => params.append(STATUS_KEY, s))
  sessionStorage.setItem(storageKey, params.toString())
}

export function WorkflowRunsTab() {
  const { projectId } = useParams()
  const { hasPermission } = useAuth(projectId)
  const { resetPage, ...pagination } = usePagination()
  const [searchParams, setSearchParams] = useSearchParams()

  const filtersStorageKey = `workflow-runs-filters-${projectId}`

  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<WorkflowRunStatus[]>(() => readStoredStatuses(filtersStorageKey))
  const [defFilter, setDefFilter] = useState('')
  const [cancelDialogRun, setCancelDialogRun] = useState<WorkflowRun | null>(null)

  const debouncedSearch = useDebounce(searchTerm)

  const sortStorageKey = `workflow-runs-sort-${projectId}`

  const hasUrlSort = SORT_KEYS.some(k => searchParams.has(k))
  const sessionSort = useMemo(
    () => new URLSearchParams(hasUrlSort ? '' : (sessionStorage.getItem(sortStorageKey) ?? '')),
    [hasUrlSort, sortStorageKey],
  )

  const sortByParam = searchParams.get('sortBy') ?? sessionSort.get('sortBy')
  const sortBy: WorkflowRunSortColumn =
    sortByParam && sortByParam in WORKFLOW_RUN_DEFAULT_SORT_DIRECTIONS ? (sortByParam as WorkflowRunSortColumn) : 'started'
  const sortDirParam = searchParams.get('sort') ?? sessionSort.get('sort')
  const sortDirection: 'asc' | 'desc' =
    sortDirParam === 'asc' || sortDirParam === 'desc' ? sortDirParam : WORKFLOW_RUN_DEFAULT_SORT_DIRECTIONS[sortBy]

  const persistSort = useCallback((params: URLSearchParams) => {
    const sortParams = new URLSearchParams()
    SORT_KEYS.forEach(k => {
      const v = params.get(k)
      if (v) sortParams.set(k, v)
    })
    sessionStorage.setItem(sortStorageKey, sortParams.toString())
  }, [sortStorageKey])

  const didRestoreSortRef = useRef(false)
  useEffect(() => {
    if (didRestoreSortRef.current) return
    didRestoreSortRef.current = true
    if (SORT_KEYS.some(k => searchParams.has(k))) return
    const saved = sessionStorage.getItem(sortStorageKey)
    if (!saved) return
    const savedParams = new URLSearchParams(saved)
    if (!Array.from(savedParams.keys()).length) return
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      savedParams.forEach((value, key) => next.set(key, value))
      return next
    }, { replace: true })
  }, [searchParams, setSearchParams, sortStorageKey])

  const handleSortChange = (column: WorkflowRunSortColumn) => {
    const nextDirection =
      column === sortBy ? (sortDirection === 'asc' ? 'desc' : 'asc') : WORKFLOW_RUN_DEFAULT_SORT_DIRECTIONS[column]
    const next = new URLSearchParams(searchParams)
    next.set('sortBy', column)
    next.set('sort', nextDirection)
    setSearchParams(next, { replace: true })
    persistSort(next)
    resetPage()
  }

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value)
  }, [])

  const handleStatusesChange = useCallback((value: WorkflowRunStatus[]) => {
    const ordered = inOptionOrder(value)
    setStatusFilter(ordered)
    storeStatuses(filtersStorageKey, ordered)
  }, [filtersStorageKey])

  const handleDefChange = useCallback((value: string) => {
    setDefFilter(value)
  }, [])

  useEffect(() => {
    resetPage()
  }, [debouncedSearch, statusFilter, defFilter, resetPage])

  const filters: RunFiltersType = useMemo(() => {
    const f: RunFiltersType = { sortBy, sortDirection }
    if (debouncedSearch.trim()) f.search = debouncedSearch.trim()
    if (statusFilter.length) f.statuses = statusFilter
    if (defFilter && defFilter !== '_all') f.definitionName = defFilter
    return f
  }, [debouncedSearch, statusFilter, defFilter, sortBy, sortDirection])

  const { data, isLoading, isError, refetch } = useWorkflowRuns(projectId!, pagination.paginationParams, filters)
  const runs = data?.items ?? []
  const totalCount = data?.totalCount ?? 0

  useNowTick(runs.some(r => isActiveRunStatus(r.status)))

  const cancelMutation = useCancelWorkflowRun(projectId!)
  const retryMutation = useRetryWorkflowRun(projectId!)
  const approveMutation = useApprovePhase(projectId!)
  const rejectMutation = useRejectPhase(projectId!)

  const { data: definitionsData } = useWorkflowDefinitions(projectId!, { skipCount: 0, maxResultCount: 100 })

  const definitions = useMemo(() => {
    if (definitionsData?.items) {
      return definitionsData.items.map(d => ({ name: d.name, displayName: d.displayName }))
    }
    return []
  }, [definitionsData])

  const canCancel = hasPermission(Permissions.Project.Workflow.Cancel, projectId)
  const canRetry = hasPermission(Permissions.Project.Workflow.Trigger, projectId)
  const canApprove = hasPermission(Permissions.Project.Workflow.Approve, projectId)

  const handleCancelClick = (runId: string) => {
    if (!canCancel) return
    const run = runs.find(r => r.id === runId)
    if (run) {
      setCancelDialogRun(run)
    }
  }

  const handleCancelConfirm = (runId: string) => {
    if (!canCancel) return
    cancelMutation.mutate(runId, {
      onSuccess: () => {
        toast.success('Run cancelled successfully')
        setCancelDialogRun(null)
      },
      onError: () => {
        toast.error('Failed to cancel run')
      }
    })
  }

  const handleRetry = (runId: string) => {
    if (!canRetry) return
    retryMutation.mutate(runId, {
      onSuccess: () => {
        toast.success('Run restarted successfully')
      },
      onError: () => {
        toast.error('Failed to retry run')
      }
    })
  }

  const handleApprove = (_runId: string, phaseId: string, comment?: string) => {
    approveMutation.mutate({ phaseId, comment }, {
      onSuccess: () => {
        toast.success('Phase approved')
      },
      onError: (error) => {
        toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to approve phase')
      }
    })
  }

  const handleReject = (_runId: string, phaseId: string, reason: string) => {
    rejectMutation.mutate({ phaseId, reason }, {
      onSuccess: () => {
        toast.success('Phase rejected')
      },
      onError: () => {
        toast.error('Failed to reject phase')
      }
    })
  }

  return (
    <div className="space-y-6">
      <WorkflowRunFilters
        search={searchTerm}
        statuses={statusFilter}
        definitionName={defFilter}
        onSearchChange={handleSearchChange}
        onStatusesChange={handleStatusesChange}
        onDefinitionChange={handleDefChange}
        definitions={definitions}
        isSortDefault={sortBy === 'started' && sortDirection === WORKFLOW_RUN_DEFAULT_SORT_DIRECTIONS.started}
        onClearAll={() => {
          setSearchTerm('')
          handleStatusesChange([])
          setDefFilter('_all')
          setSearchParams(prev => {
            const next = new URLSearchParams(prev)
            SORT_KEYS.forEach(k => next.delete(k))
            persistSort(next)
            return next
          }, { replace: true })
          resetPage()
        }}
      />

      {isLoading ? (
        <div className="space-y-3" role="status" aria-live="polite" data-testid="workflow-runs-loading">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 rounded-lg border bg-muted animate-pulse" />
          ))}
        </div>
      ) : isError ? (
        <WorkflowRunsErrorState onRetry={() => refetch()} />
      ) : (
        <>
          {runs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground" data-testid="workflow-runs-empty">
              No workflow runs found.
            </div>
          ) : (
            <div className="rounded-lg border" data-testid="workflow-runs-table">
              <Table className="table-auto" aria-label="Workflow runs">
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <SortableTableHead
                      column="title"
                      label="Title"
                      className="w-[300px]"
                      sortBy={sortBy}
                      sortDirection={sortDirection}
                      defaultDirections={WORKFLOW_RUN_DEFAULT_SORT_DIRECTIONS}
                      onSortChange={handleSortChange}
                    />
                    <SortableTableHead
                      column="linkedItem"
                      label="Linked item"
                      className="w-[105px]"
                      sortBy={sortBy}
                      sortDirection={sortDirection}
                      defaultDirections={WORKFLOW_RUN_DEFAULT_SORT_DIRECTIONS}
                      onSortChange={handleSortChange}
                    />
                    <SortableTableHead
                      column="status"
                      label="Status"
                      className="w-[125px]"
                      sortBy={sortBy}
                      sortDirection={sortDirection}
                      defaultDirections={WORKFLOW_RUN_DEFAULT_SORT_DIRECTIONS}
                      onSortChange={handleSortChange}
                    />
                    <SortableTableHead
                      column="phases"
                      label="Phases"
                      className="w-[125px]"
                      sortBy={sortBy}
                      sortDirection={sortDirection}
                      defaultDirections={WORKFLOW_RUN_DEFAULT_SORT_DIRECTIONS}
                      onSortChange={handleSortChange}
                    />
                    <SortableTableHead
                      column="started"
                      label="Started"
                      className="w-[115px]"
                      sortBy={sortBy}
                      sortDirection={sortDirection}
                      defaultDirections={WORKFLOW_RUN_DEFAULT_SORT_DIRECTIONS}
                      onSortChange={handleSortChange}
                    />
                    <SortableTableHead
                      column="duration"
                      label="Duration"
                      className="w-[70px]"
                      sortBy={sortBy}
                      sortDirection={sortDirection}
                      defaultDirections={WORKFLOW_RUN_DEFAULT_SORT_DIRECTIONS}
                      onSortChange={handleSortChange}
                    />
                    <SortableTableHead
                      column="leadTime"
                      label="Lead time"
                      className="w-[70px]"
                      sortBy={sortBy}
                      sortDirection={sortDirection}
                      defaultDirections={WORKFLOW_RUN_DEFAULT_SORT_DIRECTIONS}
                      onSortChange={handleSortChange}
                    />
                    <SortableTableHead
                      column="user"
                      label="User"
                      className="w-[150px]"
                      sortBy={sortBy}
                      sortDirection={sortDirection}
                      defaultDirections={WORKFLOW_RUN_DEFAULT_SORT_DIRECTIONS}
                      onSortChange={handleSortChange}
                    />
                    <TableHead className="w-[120px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runs.map(run => (
                    <WorkflowRunRow
                      key={run.id}
                      run={run}
                      onCancel={handleCancelClick}
                      onRetry={handleRetry}
                      onApprove={handleApprove}
                      onReject={handleReject}
                      canApprove={canApprove}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {totalCount > pagination.pageSize && (
            <Pagination
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalCount={totalCount}
              pageSizeOptions={pagination.pageSizeOptions}
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
            />
          )}
        </>
      )}

      <CancelRunDialog
        run={cancelDialogRun}
        open={!!cancelDialogRun}
        onOpenChange={(open) => !open && setCancelDialogRun(null)}
        onConfirm={handleCancelConfirm}
      />
    </div>
  )
}
